import styled, { css } from 'styled-components';
import { colors } from '@karnott/colors';
import { pixelSpacing } from '@karnott/theme';

const switchDimension = 18;
const switchBorder = 2;
const triggerSize = switchDimension - 2 * switchBorder;

const ToggleSwitchWrapper = styled.div<{
  'data-checked'?: boolean;
  checkedColor?: string;
  bare?: boolean;
  $disabled?: boolean;
}>`
  box-sizing: border-box;
  display: block;
  width: ${(props) => (props.bare ? 2 * switchDimension : 2.5 * switchDimension)}px;
  height: ${switchDimension}px;
  overflow: hidden;
  position: relative;
  border: ${switchBorder}px solid;
  border-color: ${(props) =>
    props['data-checked'] ? props.checkedColor || colors('green') : colors('grey', props.$disabled ? 300 : 400)};
  background-color: ${(props) =>
    props['data-checked'] ? props.checkedColor || colors('green') : colors('grey', props.$disabled ? 300 : 400)};
  border-radius: ${switchDimension}px;
  transition:
    border-color 150ms ease-out,
    background-color 150ms ease-out;

  [type='checkbox'] {
    display: none;
  }

  ${(props) =>
    props.$disabled
      ? null
      : css`
          &:hover {
            background-color: ${
              props['data-checked'] ? props.checkedColor || colors('green', 600) : colors('grey', 500)
            };
            border-color: ${props['data-checked'] ? props.checkedColor || colors('green', 600) : colors('grey', 500)};
          }
        `}
`;

const ToggleSwitchTrigger = styled.button<{
  checked?: boolean;
  disabled?: boolean;
}>`
  all: unset;
  box-sizing: border-box;
  position: absolute;
  background-color: #fff;
  top: 0;
  height: ${triggerSize}px;
  width: ${triggerSize}px;
  transition:
    left 150ms ease-out,
    background-color 150ms ease-out;
  border-radius: 100%;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.05);
  border: 0;
  left: ${(props) => (props.checked ? `calc(100% - ${triggerSize}px)` : 0)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  user-select: none;

  &:before {
    position: absolute;
    top: -6px;
    right: -3 * ${switchDimension}px;
    bottom: -6px;
    left: -3 * ${switchDimension}px;
    content: '';
    display: block;
  }
`;

const ToggleSwitchTrack = styled.label<{
  checked?: boolean;
  $disabled?: boolean;
}>`
  box-sizing: border-box;
  position: absolute;
  top: 0;
  left: ${(props) => (props.checked ? '-100%' : '0')};
  bottom: 0;
  width: 200%;
  color: #fff;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
`;

const ToggleSwitchLegend = styled.span`
  box-sizing: border-box;
  position: absolute;
  top: 0;
  font-size: 11px;
  padding-left: ${pixelSpacing('small')};
  padding-right: ${pixelSpacing('small')};
  width: 50%;
  display: block;
  font-weight: 500;
  line-height: ${triggerSize}px;
  user-select: none;

  &:first-child {
    left: 4px;
    text-align: right;
  }

  &:last-child {
    right: 2px;
  }
`;

type Props = {
  /** Whether the switch is on */
  checked?: boolean;
  /** The color of the checked state */
  checkedColor?: string;
  /** The callback when the switch is clicked */
  onChange?: () => void;
  /** Whether to show the "on/off" labels */
  bare?: boolean;
  /** Whether the switch can be toggled */
  disabled?: boolean;
  /** A class name for the container */
  className?: string;
};

/** A switch component that can be toggled on and off. */
export function Switch({ checked = false, checkedColor, onChange, bare = false, disabled = false, className }: Props) {
  return (
    <ToggleSwitchWrapper
      className={className}
      bare={bare}
      data-checked={checked}
      checkedColor={checkedColor}
      data-testid="karnott-sdk-switch"
      $disabled={disabled}
    >
      <ToggleSwitchTrack onClick={disabled ? undefined : onChange} checked={checked} $disabled={disabled}>
        {!bare && (
          <span>
            <ToggleSwitchLegend aria-hidden={!checked}>OFF</ToggleSwitchLegend>
            <ToggleSwitchLegend aria-hidden={checked}>ON</ToggleSwitchLegend>
          </span>
        )}
      </ToggleSwitchTrack>

      <ToggleSwitchTrigger onClick={disabled ? undefined : onChange} checked={checked} disabled={disabled} />
    </ToggleSwitchWrapper>
  );
}
